import { createContext, useContext } from 'react'
import { AutoWithdrawalsStore } from '@/stores/AutoWithdrawalsStore'
import { AdminOpsStore } from '@/stores/AdminOpsStore'
import { AdminStore } from '@/stores/AdminStore'
import { IntegrationStore } from '@/stores/IntegrationStore'
import { AuthStore } from '@/stores/AuthStore'
import { CabinetStore } from '@/stores/CabinetStore'
import { SettingsStore } from '@/stores/SettingsStore'
import { UiStore } from '@/stores/UiStore'
import { InvoicesStore } from '@/stores/InvoicesStore'
import { ReceiveStore } from '@/stores/ReceiveStore'
import { WalletsStore } from '@/stores/WalletsStore'
import { WithdrawStore } from '@/stores/WithdrawStore'
import { WithdrawalsStore } from '@/stores/WithdrawalsStore'

/**
 * Корневой стор: владеет всеми остальными и связывает их.
 *
 * Один экземпляр на приложение, раздаётся через контекст. Сторы не
 * импортируют друг друга напрямую — зависимости передаются в
 * конструкторе, иначе первая же взаимная ссылка превращается
 * в circular import, который ломается только в сборке.
 */
export class RootStore {
  readonly ui = new UiStore()
  readonly auth = new AuthStore(this.ui)
  readonly cabinet = new CabinetStore(this.ui)
  readonly wallets = new WalletsStore(this.cabinet, this.ui)
  readonly rules = new AutoWithdrawalsStore(this.cabinet, this.ui)
  readonly withdraw = new WithdrawStore(this.cabinet, this.ui)
  readonly receive = new ReceiveStore(this.ui)
  readonly withdrawals = new WithdrawalsStore(this.cabinet, this.ui)
  readonly invoices = new InvoicesStore(this.ui)
  readonly settings = new SettingsStore(this.auth, this.ui)
  readonly admin = new AdminStore(this.ui)
  readonly adminOps = new AdminOpsStore(this.ui)
  readonly integration = new IntegrationStore(this.ui)
}

const StoreContext = createContext<RootStore | null>(null)

export const StoreProvider = StoreContext.Provider

export function useStores(): RootStore {
  const store = useContext(StoreContext)
  if (store === null) {
    throw new Error('useStores вызван вне StoreProvider')
  }
  return store
}

export function useUi() { return useStores().ui }
export function useAuth() { return useStores().auth }
export function useCabinet() { return useStores().cabinet }
